/**
 * bootShell — universal boot used by runner + standalones.
 *
 * Phase 7: dispatchUrl and wireGlobalListeners stubs removed.
 * The caller (workspace/packages/workspace/index.js or a standalone
 * app's index.js) is responsible for calling dispatchUrl + wireGlobalListeners
 * AFTER bootShell returns the shell element.
 *
 * Pattern A (single-app standalones): when `apps` has exactly one entry and
 * no pre-built `context` is supplied, bootShell resolves the manifest's `app`
 * and `context` factories, mounts the shell, then auto-dispatches the current
 * URL and wires popstate. Standalone wrappers stay tiny — one bootShell call,
 * no extra imports. Multi-app shells (workspace/index.js) continue to call
 * dispatchUrl + wireGlobalListeners explicitly after bootShell returns.
 *
 * Sequence handled here:
 *  1. Early lang/theme from localStorage
 *  2. (Polyglot init — caller responsibility before bootShell)
 *  3. (ensureSDKInit — caller responsibility, or pass pre-init sdk)
 *  4. registerSdkAdapter({ sdk })
 *  5. Build/merge context state
 *  6. Auth gate via sdk.auth.getMe() — unauthed → /login, returns null
 *  7. Eager USER + ORG + WORKSPACE + WORKSPACE_PROJECT scope fetch
 *  8. Mount shell via create(app, context) — returns shell element
 *  9. [single-app only] dispatchUrl(shell, path) — initial route
 * 10. [single-app only] wireGlobalListeners(shell, ...) — popstate + storage
 *
 * Multi-app callers then:
 *  9. dispatchUrl(shell, path) — initial route (workspace/dispatch.js)
 * 10. wireGlobalListeners(shell, ...) — popstate + storage (workspace/wireGlobalListeners.js)
 */

// Side-effect import — pins globalThis.WebSocket + scrubs Parcel's
// process.versions.node polyfill so a USER project's own supabase-js client
// (the bring-your-own-backend fetch adapter — a framework feature; the
// PLATFORM has no Supabase) constructs correctly inside the canvas. See
// bootShim.js for the Parcel Node-detection rationale.
import './bootShim.js'
import { registerAdapter } from '@symbo.ls/fetch'
import { buildSdkAdapter } from './sdkAdapter.js'
import { resolveAuthNext } from './resolveAuthNext.js'
import { currentChannelFromHost } from './canvasUrl.js'
// Opt-in boot breadcrumbs — see bootTrail.js for why a wedged renderer
// cannot be diagnosed by any probe that runs after the fact.
import { bootTrail } from './bootTrail.js'
import { yieldToPaint } from './yieldToPaint.js'
import { setActiveWorkspaceRecord } from './activeWorkspaceRecord.js'

// FA204 / frank trap — `_resolveAuthSnapshot` and `_authSnapshotFromCache`
// (below, "Warm-start PAINT half") used to live at module scope, referenced
// bare from `bootShell`, their only caller (project_frank_drops_module_
// local_helpers). Both now live nested INSIDE `bootResolve`'s own body
// (nested declarations inside the exported function's own source are never
// rewritten by frank and keep working via normal JS closures) — see the
// top of `bootResolve` (bootResolve.js) for their bodies, unchanged.

// Does `err` (or anything in its `.cause` chain) prove the SERVER rejected
// the bearer token, as opposed to an ambiguous/transient failure (network
// error, 5xx, timeout, ...)? Pure — no closure dependency — so it's hoisted
// to module scope (out of `bootShell`'s body, where it used to live inline)
// and exported for unit testing. Callers: `bootShell`'s auth gate. Only a
// `true` result may destroy stored credentials; every other outcome must
// preserve them (see the `_getMeUnresolved` handling inside `bootShell`).
//
// Checks structured `err.code === 'invalid_token'` (server@70ecefdf+), then
// `err.status === 401`, then a message-regex fallback for older emitters
// (covers HMAC rotation, federation refusal, the cross-env "missing
// workspace claim" symptom from JWT_SECRET drift, etc.).
//
// #724: the server's standardized 401 envelope (server/src/core/utils/
// authError.js — `{ status: 401, body: { error: 'invalid_token', code:
// 'invalid_token', reason, message } }`) survives on the error the SDK's
// `BaseService._request` throws (`.status` + `.cause` = the JSON body), but
// service-level wrappers re-wrap AGAIN to prefix context onto the message
// (e.g. `AuthService._fetchMe`: `new Error('Failed to get user profile: ' +
// original.message, { cause: original })`), which pushes `.status`/`.code`
// one level deeper into `.cause` on every wrap. Reading only the outer
// `err.status`/`err.code` (as this used to) therefore MISSES every real
// rejection surfaced through `getMe()` — falling through to the message
// regex, which also doesn't match the human-readable DEFAULT_MESSAGES
// wording ("Token has expired; sign in again to refresh.") authError.js
// sends today. Walk the `.cause` chain so a rejection is detected
// regardless of how many layers re-wrapped it.
// @testOnly — exported for bootShell.authClassification.test.js; no live code imports or calls it.
export const _causeChain = (err, maxDepth = 4) => {
  const chain = []
  let cur = err
  for (let i = 0; cur && i < maxDepth; i++) {
    chain.push(cur)
    cur = cur.cause
  }
  return chain
}
// @testOnly — exported for bootShell.authClassification.test.js; no live code imports or calls it.
export const _isTokenRejection = (err) => {
  // FA204 / frank trap — self-contained inline copy of _causeChain (a bare
  // sibling-export call is exactly as unresolvable post-frank.toJSON as a
  // module-local read); the export above stays live for its own unit tests.
  const _causeChainInline = (err, maxDepth = 4) => {
    const chain = []
    let cur = err
    for (let i = 0; cur && i < maxDepth; i++) {
      chain.push(cur)
      cur = cur.cause
    }
    return chain
  }
  if (!err) return false
  // A numeric status anywhere in the chain is the server's own answer: only
  // 401 (or the explicit `invalid_token` code) means the token was refused.
  // Any other status — a 500 whose message happens to read "No workspace…" /
  // "unauthorized" — is NOT a rejection, so the prose fallback below runs
  // only when no status exists at all (a session must never be purged on a
  // server error).
  let sawStatus = false
  for (const e of _causeChainInline(err)) {
    const code = e?.code || e?.body?.code || e?.response?.body?.code
    if (code === 'invalid_token') return true
    const status = e?.status || e?.statusCode || e?.response?.status
    if (status === 401) return true
    if (typeof status === 'number') sawStatus = true
  }
  if (sawStatus) return false
  const s = String(err?.message || err || '')
  if (!s) return false
  return /\binvalid (?:or expired )?(?:access )?token\b|\btoken (?:signature is invalid|has expired|is malformed|has been revoked)\b|\bno bearer token\b|\binvalid signature\b|signature[_\s]+mismatch|\bjwt (?:malformed|expired)\b|missing\s+(?:workspace|user|active[\s_]?workspace)?[\s_]*claim|no\s+(?:workspace|active workspace)|\bunauthor(?:i[sz]ed)?\b|HTTP 401\b|Request failed:.*401/i.test(
    s
  )
}

// AUTH-WIPE-1 (tickets/bellforge.md) — Nika, 2026-08-18, chose (c): scope
// the leniency to DEV ONLY. A server verifying with the WRONG JWT secret
// classifies EVERY token as `reason: 'signature_mismatch'` — the SAME class
// a genuinely revoked/rotated token produces — so the two are NOT
// distinguishable from the error shape alone.
// ⚠️ MATCH THE STRUCTURED FIELD, NOT THE PROSE. The client never receives
// jsonwebtoken's internal `invalid signature`; authError.js consumes it and
// sends `{ code:'invalid_token', reason:'signature_mismatch', message:
// 'Token signature is invalid; sign in again to refresh.' }`. A first cut
// of this guard matched only the internal string and was therefore INERT —
// it would have shipped green against a fixture derived from its own regex.
// The fixture belongs to the PRODUCER (server/src/core/utils/authError.js),
// never to this consumer's assumption about it. They ARE distinguishable by
// HOST: only a `local`-channel dev API restarts without its secret in the
// way this incident described. Every other channel (development, next,
// staging, production, and unknown/null on a server render) keeps treating
// a signature-class 401 as a proven rejection — fail-closed, unchanged.
// Pure, no closure dependency — hoisted to module scope and exported for
// unit testing, same shape as `_isTokenRejection` above. `bootShell` keeps
// its own self-contained inline copy (FA204 / frank trap — a bare
// same-file sibling-export call is unresolvable post-frank.toJSON;
// `currentChannelFromHost` is a cross-file ESM import so it is called
// directly in both copies, never inlined — see canvasUrl.js's own FA204
// note for why cross-file imports aren't part of that trap).
// @testOnly — exported for bootShell.devInfraSignature.test.js; no live code imports or calls it.
export const _isDevInfraSignatureWindow = (err) => {
  if (currentChannelFromHost() !== 'local') return false
  const reasons = []
  const codes = []
  const parts = []
  let cur = err
  for (let i = 0; cur && i < 4; i++) {
    reasons.push(String(cur.reason || cur.body?.reason || cur.response?.body?.reason || ''))
    codes.push(String(cur.code || cur.body?.code || cur.response?.body?.code || ''))
    parts.push(String(cur.message || cur.body?.message || ''))
    cur = cur.cause
  }
  // A KNOWN reason of any other class is decisive and fail-closed —
  // expired / revoked / malformed / missing still purge, even on local.
  // This is what makes "signature-class ONLY" true by CONSTRUCTION rather
  // than by prose matching.
  if (reasons.some((r) => r && r !== 'signature_mismatch')) return false
  if (reasons.includes('signature_mismatch')) return true
  if (codes.includes('invalid_signature')) return true
  // Prose fallback ONLY. Note `token signature is invalid` — the wording
  // authError.js actually sends. The bare `invalid signature` alternative
  // is jsonwebtoken's INTERNAL string, which the server consumes and
  // replaces; it is kept only for a raw/unenveloped path.
  return /\btoken signature is invalid\b|\binvalid signature\b|signature[_\s]+mismatch/i.test(
    parts.join(' ')
  )
}

// Module-side-effect: eagerly register the 'sdk' fetch adapter with a
// sdk-less loader at the TOP of the universal boot file. Lives here
// instead of inside sdkAdapter.js because Parcel was tree-shaking the
// trailing `registerAdapter('sdk', …)` from that file despite
// `"sideEffects": true` in packages/shared/package.json.
//
// Why eager: workspace-project/config.js declares
// `fetch: { adapter: 'sdk', sdk: getSDK() }` at module-load time, and
// any DOMQL element that consumes that decl fires `resolveDb` during
// create() — which throws "Unknown db adapter: 'sdk'" until something
// registers the adapter. bootShell's step-4 `registerSdkAdapter({ sdk })`
// runs LATER (after the auth gate), so element-init before that point
// crashes. Eager registration closes the race; the explicit-bound-sdk
// call still runs later and harmlessly overrides — both paths produce
// identical adapters because `buildSdkAdapter` resolves `sdk` from
// boundOptions OR the per-decl config (decl wins on key collision).
registerAdapter('sdk', () =>
  Promise.resolve({
    setup: (decl = {}) => buildSdkAdapter(decl)
  })
)

// FA204 / frank trap — `_singleAppDispatch`/`_singleAppWireListeners` used
// to live at module scope, referenced bare from `bootShell`, their only
// caller. Both are now nested INSIDE `bootShell`'s own body (see the top of
// `bootShell` below for their bodies, unchanged).

/**
 * @param {object} params
 * @param {object}   params.apps            - map of app definitions / manifests
 * @param {object}   params.defaultApp      - manifest for the default app (object or key string)
 * @param {string}   params.fallbackRoute   - route to use when dispatch fails
 * @param {object}   [params.sdk]           - pre-initialised SDK instance
 * @param {object}   [params.context]       - pre-built DOMQL context object
 * @param {object}   [params.getWorkspaceCached] - DI'd getWorkspace coalescer (#17), see below
 * @param {object}   [params.warmSnapshot]  - an ALREADY-VALIDATED cached boot
 *   snapshot row (from `packages/workspace/functions/bootSnapshot.js`'s
 *   `resolveWarmBootSnapshot()`), or null/omitted. When present + it maps to
 *   a usable identity, the shell mounts from it immediately — BEFORE this
 *   function's own auth-gate network round trip below even starts — and
 *   reconciles with the live result once that round trip resolves. See the
 *   "Warm-start PAINT half" block a few hundred lines down for the design;
 *   omitted/null is byte-identical to today's behavior (every caller that
 *   doesn't pass this is completely unaffected).
 * @returns {Promise<object|null>} shell element from create(), or null on auth bounce
 */

export const bootShell = async ({
  apps,
  defaultApp,
  fallbackRoute,
  sdk,
  context,
  publicSurface = false,
  // Optional dependency-injected getWorkspace coalescer (#17). The workspace
  // passes its `getWorkspaceCached` so bootShell's eager scope-fetch shares the
  // in-flight-dedup + short-TTL cache with loadWorkspaceProject / onWorkspaceChange
  // — collapsing the duplicate boot getWorkspace round-trip. bootShell lives in
  // `packages/shared` and can't import `packages/workspace`, hence injection.
  // Consumers that don't pass it (preview) fall back to the raw sdk.getWorkspace.
  getWorkspaceCached = null,
  // Optional DI (the workspace's `primeWorkspaceCache`): seeds that cache with
  // the boot's full-projection workspace rows, so the record is never read a
  // second time on a cold load (see the prime call after sdk.boot()).
  primeWorkspaceCache = null,
  warmSnapshot = null,
  // The three boot steps (bootPrepare / bootResolve / bootApply), reached
  // through the caller's functions registry — every entry passes
  // `steps: _wsShared.functions`. bootShell imports none of them (FA001):
  // the published shell runs the SAME three from its boot plugin.
  steps = null
}) => {
  // Opt-in boot breadcrumbs (`?trail=1`), inert otherwise — the only way to
  // see inside a boot that wedges the renderer. See bootTrail.js.
  /**
   * Minimal single-app dispatch + popstate wiring used when bootShell
   * auto-boots a standalone (single-entry apps map).
   *
   * Routes are sourced from the manifest's `routes` array (RegExp or string).
   * If the current path matches, the manifest's `app` factory is invoked and
   * the result is set into shell.Slot (if present) or rendered as the root.
   * Falls back to fallbackRoute when no match is found.
   *
   * This is intentionally simpler than workspace/dispatch.js — standalones
   * mount a single app and don't need a multi-app dispatch table.
   */
  // URL-fallback half of the single-app dispatch, on its own so the cold
  // mount below can run it BEFORE awaiting `create()` — the DOMQL router
  // reads window.location on its first dispatch, which happens inside the
  // awaited mount; this rewrite has to land ahead of it (see the comment
  // in the body). `singleAppDispatch` still calls it, so the popstate
  // re-dispatch path is unchanged.
  function singleAppUrlFallback(manifest, path, fallbackRoute) {
    const routes = manifest.routes || []
    // Route regexps anchor on the pathname segment only (e.g.
    // `^\/marketplace(\/|$)`). Query strings would push the `$` boundary
    // out of range and turn a deep-link like `/marketplace?item=X` into
    // an unmatched route — bootShell would then rewrite to fallbackRoute
    // and strip the query. Strip the query before testing so deep links
    // survive the dispatch.
    const pathOnly = path.split('?')[0]
    const matched = routes.some((r) => {
      if (r instanceof RegExp) return r.test(pathOnly)
      if (typeof r === 'string') return r === pathOnly
      return false
    })
    // When no manifest route matches and a fallbackRoute is configured,
    // rewrite the URL bar to the fallback BEFORE mounting so the DOMQL
    // router has a path it can resolve. Without this, the router warns
    // "no content matched for path X" and renders nothing — visible as a
    // blank page on any unmapped URL (e.g. /login on the preview origin,
    // see https://silverbreeze-labs--hotcard.dev.preview.symbols.app/login
    // bug). The router reads window.location itself on init, so a
    // replaceState here is enough — no explicit dispatch call needed.
    if (
      !matched &&
      typeof fallbackRoute === 'string' &&
      fallbackRoute &&
      typeof window !== 'undefined'
    ) {
      try {
        window.history.replaceState(null, '', fallbackRoute)
      } catch (_) {}
    }
  }

  async function singleAppDispatch(shell, manifest, path, fallbackRoute) {
    singleAppUrlFallback(manifest, path, fallbackRoute)
    if (shell && shell.Slot && typeof shell.Slot.set === 'function') {
      // Multi-slot shell: set the app into the Slot
      const appDef = await manifest.app?.().catch(() => null)
      if (appDef) shell.Slot.set(appDef)
    }
    // If there is no Slot, the app is already mounted as the root (create()
    // used context.app directly). Nothing more to do for dispatch.
  }

  /**
   * Minimal popstate wiring for single-app standalones.
   * Re-dispatches on browser back/forward.
   */
  function singleAppWireListeners(shell, manifest, fallbackRoute) {
    window.addEventListener('popstate', () => {
      const path = window.location.pathname + window.location.search
      singleAppDispatch(shell, manifest, path, fallbackRoute)
    })
  }

  // Cross-origin auth bounce — public surfaces (preview) don't host a
  // login UI; auth lives on the workspace shell. If a user lands on
  // `<project>.<tier>.preview.symbols.app/login?next=…` (stale link or
  // direct paste), redirect to `<tier>.my.symbols.app/login?next=…`
  // before doing any other work. Without this, the preview app mounts
  // with no matching route and renders a blank screen — see the bug
  // report at /login?next=%2F on the dev preview origin.
  //
  // Restricted to `*.preview.symbols.app` so localhost / canvas dev
  // workflows are untouched. The auth-path regex matches the same set
  // PUBLIC_AUTH_ROUTE accepts on the shell side (login/signin/signup/
  // reset-password/forgot-password), so anything the shell treats as a
  // login surface is bounced consistently.
  if (publicSurface && typeof window !== 'undefined') {
    const _host = window.location.hostname
    const _isAuthPath = /^\/(login|signin|signup|reset-password|forgot-password)(\/|$)/.test(
      window.location.pathname
    )
    const _previewMatch = _host.match(
      /(?:^|\.)((?:dev|next|test|upcoming|staging)\.)?preview\.symbols\.app$/
    )
    if (_isAuthPath && _previewMatch) {
      const tierPrefix = _previewMatch[1] || ''
      const shellHost = `${tierPrefix}my.symbols.app`
      // LOGINLOOP-RELEASE-1: resolve through resolveAuthNext so a carried
      // `next` is reused, never re-wrapped (fifth bounce site).
      const _dest = resolveAuthNext(window.location.pathname, window.location.search)
      const next = _dest ? `?next=${encodeURIComponent(_dest)}` : ''
      window.location.replace(`https://${shellHost}/login${next}`)
      return null
    }
  }

  // Pattern A: single-app standalone — resolve manifest factories eagerly
  // so the shell is created with a real app + context instead of an empty
  // placeholder. Multi-app callers always supply a pre-built context, so
  // this branch is only entered for standalones.
  const appKeys = Object.keys(apps || {})
  const isSingleApp = appKeys.length === 1 && !context

  let resolvedManifest = null
  if (isSingleApp) {
    resolvedManifest = apps[appKeys[0]]
    // Resolve context factory if the manifest provides one
    const manifestContext =
      typeof resolvedManifest.context === 'function'
        ? await resolvedManifest.context().catch(() => ({}))
        : resolvedManifest.context || {}
    // Resolve app factory if context.app is not already set
    if (!manifestContext.app && typeof resolvedManifest.app === 'function') {
      manifestContext.app = await resolvedManifest.app().catch(() => ({}))
    }
    context = manifestContext
  }

  // Default to empty context when neither caller nor manifest provides one
  if (!context) context = {}


  // ── THE ONE BOOT SEQUENCE ──────────────────────────────────────────────
  // bootPrepare → bootResolve → create() → bootApply. The published shell
  // runs the same three steps (packages/workspace/context.js boot plugin);
  // this composer adds only what a parcel entry has and a published page
  // does not: the host-package tier (4b — mermaid's page registers it
  // itself), the warm-start early mount and the cold create().
  const _steps = steps || (context && context.functions) || {}
  if (
    typeof _steps.bootPrepare !== 'function' ||
    typeof _steps.bootResolve !== 'function' ||
    typeof _steps.bootApply !== 'function'
  ) {
    throw new Error(
      '[bootShell] the boot steps are missing — pass \`steps: _wsShared.functions\` ' +
        '(bootPrepare / bootResolve / bootApply)'
    )
  }
  // Tells the published-shell boot plugin (beforeCreate) that this boot
  // already ran: nothing runs twice.
  context.bootMode = 'parcel'

  const _prep = _steps.bootPrepare(context, {
    sdk,
    publicSurface,
    warmSnapshot,
    forceSdk: isSingleApp
  })
  sdk = _prep.sdk
  const lang = _prep.lang

  // 4b. HOST-REGISTERED PACKAGE TIER — WS-HOST-REGISTER-SDK-1 (Nika answer B,
  // 2026-08-21). `@symbo.ls/sdk` is deliberately NOT in the public smbls
  // framework bundle (FRAMEWORK-REGISTRY-RESTRICTED-IMPORTS-1 answer B: the
  // platform, not smbls, owns the platform client). frank therefore emits, for
  // every serialized project function that reads an sdk export, a SYNCHRONOUS
  // `globalThis.__SMBLS_PKGS__['@symbo.ls/sdk']` read plus a NAMED throw on a
  // miss — there is no `await import()` fallback on purpose, because the
  // package returns npm E404 on the public registry.
  //
  // Every host that bundles smbls must therefore hand smbls its OWN sdk copy
  // before the first render. This is the single funnel: canvas, preview, the
  // shell and marketplace all `await bootShell(...)`, and it runs before BOTH
  // create() paths below (the warm-start early mount and the cold mount).
  //
  // ⚠️ The value MUST be the sdk MODULE NAMESPACE, never the `getSDK()`
  // INSTANCE. frank emits `__mod.environment`,
  // `__mod.PROJECT_ROLE_PERMISSIONS`, `__mod.readCookie`, `__mod.writeCookie`,
  // `__mod.createCrossAppAuth` — all module-level exports. Registering the
  // instance satisfies the registry read and then fails one step later with
  // the cause gone.
  //
  // Registration is ADDITIVE: it never swaps a namespace already on the store,
  // so two boot paths in one realm cannot fight. Safe since sdk 295efd2 parks
  // the default TokenManager on globalThis — a second evaluated sdk copy no
  // longer means two TokenManagers rotating one refresh token in one
  // localStorage.
  //
  // Guarded because an older `smbls` (< 3.14.786) has no such export; there the
  // sites keep exactly today's named throw instead of taking the whole app down
  // with a TypeError at boot.
  try {
    const smblsModule = await import('smbls')
    if (typeof smblsModule.registerHostPackages === 'function') {
      // The sdk MODULE NAMESPACE (not the instance), imported at call time: a
      // module-scope `import * as` read here is unresolvable in frank's
      // emission. No added bundle weight — `./sdk.js` already pulls
      // @symbo.ls/sdk into every host that calls bootShell.
      const sdkModule = await import('@symbo.ls/sdk')
      smblsModule.registerHostPackages({ '@symbo.ls/sdk': sdkModule })
      bootTrail('bootShell:hostPackages')
    } else {
      console.warn(
        '[bootShell] this smbls build has no registerHostPackages — serialized ' +
          'project functions that read @symbo.ls/sdk keep throwing their named ' +
          'host-package error (needs smbls >= 3.14.786)'
      )
    }
  } catch (err) {
    console.warn('[bootShell] host package registration failed:', err?.message || err)
  }


  // The warm-start early mount (a parcel speed path): bootResolve calls it
  // after seeding the cached auth snapshot, and owns its disposal.
  const _mountEarly = async (themeModeNow) => {
    const { create: _createEarly } = await import('smbls')
    const shellApp = context.app || {}
    await yieldToPaint()
    return _createEarly(shellApp, {
      ...context,
      globalTheme: themeModeNow,
      themeStorageKey: 'themeMode',
      domqlOptions: { ...context.domqlOptions, mountYield: true }
    })
  }

  const _res = await _steps.bootResolve(context, _prep, {
    publicSurface,
    warmSnapshot,
    getWorkspaceCached,
    primeWorkspaceCache,
    apps,
    defaultApp,
    resolvedManifest,
    fallbackRoute,
    mountEarly: _mountEarly
  })
  if (!_res || _res.aborted) return null
  const themeMode = _res.themeMode
  const _earlyShell = _res.earlyShell
  // Read by the shell root's own lifecycle: the boot has settled.
  context.bootReady = Promise.resolve(_res)

  // 8. Mount shell
  // The context.app is the shell DOMQL root component (app.js default export).
  // If context.app is not set, fall back to a minimal empty object so
  // bootShell is still usable in tests without a full app definition.
  // Pass the project's themeMode so smbls uses the same mode the workspace
  // chrome shows. `auto` remains `auto`; scratch resolves it and owns the
  // document-level data-theme updates.
  const { create } = await import('smbls')
  const shellApp = context.app || {}
  let shell
  if (_earlyShell) {
    // Warm-start: already mounted (and already painting) from the cached
    // snapshot before this function's own auth-gate/scope-fetch network
    // calls above even started. Reconcile the live root with whatever those
    // UNCHANGED calls actually resolved to — `context.preResolvedAuth` /
    // `context.state` here are the exact same values the cold path just
    // below would otherwise feed into `create()` for a first-ever mount.
    shell = _earlyShell
    if (context.preResolvedAuth) {
      // `workspace` is deliberately excluded from this bare update and
      // handled by `setActiveWorkspaceRecord` instead, below — the ONE
      // setter allowed to write `root.workspace` post-mount (see
      // activeWorkspaceRecord.js: a bare `state.update({ workspace })` here
      // risks exactly the list-over-full downgrade race
      // tickets/spec-workspace-record-shape.md closed). The cache seeded a
      // 'list'-shaped placeholder; by this point `context.state.workspace`
      // is 'full'-shaped whenever the eager fetch above succeeded (the
      // common case), so the setter's own "full replacing anything →
      // accept" rule takes it — an ordinary upgrade, not a special case.
      const { workspace: _workspaceHandledBelow, ...restAuth } = context.preResolvedAuth
      shell.state.update({ ...restAuth, _bootFromCache: false })
      // Only call the setter when the cold path actually resolved SOMETHING
      // (the composite-boot fast path or the eager sdk.getWorkspace fetch,
      // a few hundred lines up — both gated on `activeWorkspaceId` being
      // truthy, which it always is for a scope that just produced a cache
      // hit). `setActiveWorkspaceRecord(root, null, …)` is an EXPLICIT clear
      // (sign-out / no active workspace) — passing it a merely-unresolved
      // `context.state.workspace` on some degenerate structural path (e.g.
      // `sdk.getWorkspace` not a function) would wipe the cache-painted
      // workspace instead of just leaving an unconfirmed-but-plausible guess
      // in place. Leaving it untouched here is strictly safer: the ticket's
      // own bar is "never paint wrong-scope," not "always resolve every
      // field" — an un-upgraded 'list' placeholder the user already sees is
      // not a regression from today's cold-boot experience.
      if (context.state.workspace) {
        setActiveWorkspaceRecord(shell.state, context.state.workspace, {
          reason: 'boot-warm-reconcile',
          sdk,
          getWorkspaceCached
        })
      }
    } else {
      // The real auth gate never resolved a user on this pass — shouldn't
      // happen for a cache hit (the token that keyed the cache was live
      // moments ago), but never leave the cache flag stuck true regardless.
      shell.state.update({ _bootFromCache: false })
    }
  } else {
    // `create()` returns a PROMISE of the root element (smbls/src/index.js:
    // `createDomqlElement(...).then(...)`). It MUST be awaited here: the
    // watcher below reads `shell.state`, and `.state` of a Promise is
    // `undefined` — the un-awaited version silently skipped
    // `watchThemeResolved` on EVERY cold boot, so `root.themeResolved` froze
    // at its boot seed while `data-theme` kept moving (WS-THEME-BOOT-WRONG-
    // MODE-1: rail/widget-ink JS readers disagreeing with CSS — the "partly
    // light" report; measured live on staging 2026-08-19:
    // `document.__themeResolvedWatcher` absent). The caller awaits
    // bootShell's return regardless, so awaiting here changes nothing for
    // it; the warm-start branch above already awaited its own mount.
    //
    // Single-app standalones (canvas/preview/marketplace/analytics): the
    // URL-fallback rewrite used to ride `singleAppDispatch` right after the
    // un-awaited `create()` call, i.e. synchronously BEFORE the router's
    // first dispatch inside the mount. Awaiting the mount would flip that
    // order and bring back the blank page on an unmapped URL, so the
    // rewrite runs here first, explicitly, with the same inputs.
    if (isSingleApp && resolvedManifest && typeof window !== 'undefined') {
      singleAppUrlFallback(
        resolvedManifest,
        window.location.pathname + window.location.search,
        fallbackRoute
      )
    }
    bootTrail('bootShell:create:start')
    // Same frame yield as the warm-start mount above. Here the boot is
    // resuming inside the SDK response continuation, so the tree build was
    // absorbed into that task — LoAF measured it as a single 1726 ms
    // `resolve-promise` frame on my.symbols.app (2026-08-22). Yielding
    // closes that task first, so pending input and the spiral's own
    // animation get served before the build starts.
    await yieldToPaint()
    // Same mount-scheduler opt-in as the warm-start mount above — see the
    // full rationale there. This is the path that matters most for the
    // number: a cold boot builds the whole chrome AND the entry route in one
    // recursion, which is the 594 ms task the yield alone could only move.
    shell = await create(shellApp, {
      ...context,
      globalTheme: themeMode,
      themeStorageKey: 'themeMode',
      domqlOptions: { ...context.domqlOptions, mountYield: true }
    })
  }


  await _steps.bootApply(shell, _res, { themeApplied: true })

  if (isSingleApp && resolvedManifest) {
    const path = window.location.pathname + window.location.search
    await singleAppDispatch(shell, resolvedManifest, path, fallbackRoute)
    singleAppWireListeners(shell, resolvedManifest, fallbackRoute)
  }

  return shell
}
